import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { settingsApi } from '../services/settingsApi';
import type { Role, UserRole } from '../types/settings';
import { AlertCircle, Search } from 'lucide-react';

interface EntraUser {
    email: string;
    displayName: string;
}

export function UserManagementPage() {
    const { user } = useAuth();
    const [users, setUsers] = useState<UserRole[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [entraUsers, setEntraUsers] = useState<EntraUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [newEmail, setNewEmail] = useState('');
    const [newRoleId, setNewRoleId] = useState<number | ''>('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        setLoading(true);
        setError(null);
        try {
            const [usersData, rolesData, entraData] = await Promise.all([
                settingsApi.getUsers(),
                settingsApi.getRoles(),
                settingsApi.getEntraUsers().catch(() => ({ users: [] as EntraUser[] })),
            ]);
            setUsers(usersData);
            setRoles(rolesData);
            setEntraUsers(entraData.users || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }

    const existingEmails = new Set(users.map(u => u.Email.toLowerCase()));
    const availableEntraUsers = entraUsers.filter(eu => !existingEmails.has(eu.email.toLowerCase()));

    const filteredUsers = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return users;
        return users.filter(u =>
            u.Email.toLowerCase().includes(q) ||
            (u.FirstName ?? '').toLowerCase().includes(q) ||
            (u.LastName ?? '').toLowerCase().includes(q) ||
            (u.DisplayName ?? '').toLowerCase().includes(q)
        );
    }, [users, searchTerm]);

    function formatName(u: UserRole): string {
        const full = `${u.FirstName ?? ''} ${u.LastName ?? ''}`.trim();
        return full || (u.DisplayName ?? '') || '—';
    }

    async function handleCreateUser(e: React.FormEvent) {
        e.preventDefault();
        if (!newEmail || newRoleId === '') return;

        try {
            await settingsApi.createUser(newEmail, newRoleId);
            setNewEmail('');
            setNewRoleId('');
            loadData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create user');
        }
    }

    async function handleToggleUser(id: number, currentActive: boolean) {
        try {
            await settingsApi.updateUser(id, { isActive: !currentActive });
            loadData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update user');
        }
    }

    if (!user?.isAdmin) {
        return (
            <div className="flex items-center gap-3 p-6 bg-red-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-700">Access denied. Admin role required.</p>
            </div>
        );
    }

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">User Management</h1>

            {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                    <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
                        Dismiss
                    </button>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center p-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Add User Form */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold mb-4">Add User</h2>
                        <form onSubmit={handleCreateUser} className="flex gap-4 items-end">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    User (Entra Group: ReportingApp)
                                </label>
                                <select
                                    value={newEmail}
                                    onChange={e => setNewEmail(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Select user...</option>
                                    {availableEntraUsers.map(eu => (
                                        <option key={eu.email} value={eu.email}>
                                            {eu.displayName} ({eu.email})
                                        </option>
                                    ))}
                                </select>
                                {entraUsers.length === 0 && (
                                    <p className="mt-1 text-xs text-gray-400">
                                        Entra group not loaded. You can type an email below.
                                    </p>
                                )}
                            </div>
                            <div className="w-48">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                    value={newRoleId}
                                    onChange={e => setNewRoleId(Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Select role...</option>
                                    {roles.map(r => (
                                        <option key={r.Id} value={r.Id}>{r.RoleName}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                            >
                                Add User
                            </button>
                        </form>
                    </div>

                    {/* User List */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-4 border-b border-gray-200">
                            <div className="relative max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredUsers.map(u => (
                                    <tr key={u.Id} className={!u.IsActive ? 'opacity-50' : ''}>
                                        <td className="px-6 py-4 text-sm text-gray-900">{formatName(u)}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{u.Email}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{u.RoleName}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                                                u.IsActive
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-500'
                                            }`}>
                                                {u.IsActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleToggleUser(u.Id, u.IsActive)}
                                                className="text-sm text-blue-600 hover:text-blue-800"
                                            >
                                                {u.IsActive ? 'Deactivate' : 'Activate'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredUsers.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-8 text-center text-gray-500 text-sm">
                                            {users.length === 0
                                                ? 'No users found. Add a user above.'
                                                : 'No users match your search.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
